import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useLogout } from "ra-core";
import { useNavigate, useParams } from "react-router-dom";
import {
  CalendarX,
  CheckCircle2,
  Clock,
  QrCode,
  ShieldAlert,
  UserX,
  type LucideIcon,
} from "lucide-react";

import { getFraktionLabel } from "@/components/atomic-crm/misc/fraktionen";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { SystemThemeSync } from "../../navigation/SystemThemeSync";
import {
  checkIn,
  loadCheckIn,
  requestLate,
  type CheckInMe,
} from "./checkInApi";
import {
  checkInErrorView,
  checkInResultView,
  checkInWindow,
  formatSessionWhen,
  formatTime,
  initialView,
  lateRequestErrorText,
  lateRequestSession,
  normalizeCode,
  pickTodayEntry,
  TIME_ZONE,
  type CheckInSession,
  type CheckInView,
} from "./checkInState";

const monthName = (iso: string) =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: TIME_ZONE,
    month: "long",
  }).format(new Date(iso));

const viewSession = (view: CheckInView | null): CheckInSession | null =>
  view && "session" in view ? view.session : null;

/** Phone page behind the room QR: one tap marks the signed-in person present. */
export const CheckInPage = () => {
  const { code: rawCode } = useParams();
  const code = normalizeCode(rawCode);
  const path = code ? `/check-in/${encodeURIComponent(code)}` : "/check-in";
  const navigate = useNavigate();
  const logout = useLogout();

  const [me, setMe] = useState<CheckInMe | null>(null);
  const [view, setView] = useState<CheckInView | null>(null);
  const [busy, setBusy] = useState(false);
  const [lateError, setLateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setView(null);
    setLateError(null);
    try {
      const now = new Date();
      const loaded = await loadCheckIn(now);
      if (!loaded.signedIn) {
        navigate(`/login?redirect=${encodeURIComponent(path)}`, {
          replace: true,
        });
        return;
      }
      if (!loaded.me) {
        setView({
          kind: "error",
          message:
            "Zu deiner Anmeldung gibt es kein Konto im Hub. Bitte das Fraktionsbüro fragen.",
        });
        return;
      }
      setMe(loaded.me);
      setView(initialView(code, pickTodayEntry(loaded.entries, now), now));
    } catch {
      setView({
        kind: "error",
        message: "Die Seite konnte nicht laden. Bitte noch einmal versuchen.",
      });
    }
  }, [code, navigate, path]);

  useEffect(() => {
    load();
  }, [load]);

  const session = viewSession(view);

  const onCheckIn = async () => {
    if (!code || busy) return;
    setBusy(true);
    const known = session;
    const answer = await checkIn(code);
    setView(
      "data" in answer
        ? checkInResultView(answer.data, known)
        : checkInErrorView(answer.error, known, new Date()),
    );
    setBusy(false);
  };

  const late = view ? lateRequestSession(view, new Date()) : null;
  const onLate = async () => {
    if (!late || busy) return;
    setBusy(true);
    setLateError(null);
    const { error } = await requestLate(late.id);
    if (error) setLateError(lateRequestErrorText(error));
    else setView({ kind: "requested", session: late });
    setBusy(false);
  };

  const district = session?.district ?? me?.fraktion ?? null;
  const fraktionName = district
    ? `Volt Fraktion HH-${getFraktionLabel(district)}`
    : "Volt Hamburg Hub";
  const myFunction =
    me?.functions.find((f) => f.district === district)?.function ??
    me?.functions[0]?.function;

  return (
    <div className="min-h-svh bg-background text-foreground">
      <SystemThemeSync />
      <header className="bg-primary px-4 py-3 text-sm font-medium text-primary-foreground">
        {fraktionName} · Anwesenheit
      </header>
      <main className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 py-5">
        {!view ? (
          <>
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </>
        ) : (
          <>
            {session && view.kind !== "requested" ? (
              <SessionCard session={session} />
            ) : null}
            {me ? (
              <section className="rounded-lg border bg-card p-4">
                <div className="text-xs font-semibold tracking-wide text-muted-foreground">
                  DU
                </div>
                <div className="mt-1 text-base font-semibold break-words">
                  {me.fullName}
                </div>
                <div className="text-sm text-muted-foreground break-words">
                  {[
                    myFunction,
                    district
                      ? `Fraktion HH-${getFraktionLabel(district)}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </section>
            ) : null}

            <ViewBody
              view={view}
              code={code}
              busy={busy}
              onCheckIn={onCheckIn}
              onReload={load}
            />

            {late ? (
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="h-12 w-full text-base"
                  disabled={busy}
                  onClick={onLate}
                >
                  Nachtrag anfragen
                </Button>
                {lateError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {lateError}
                  </p>
                ) : null}
              </div>
            ) : null}

            {me ? (
              <p className="pt-2 text-center text-sm text-muted-foreground">
                Nicht {me.firstName || "du"}?{" "}
                <button
                  type="button"
                  className="min-h-12 px-1 font-medium text-primary underline-offset-4 hover:underline"
                  onClick={() =>
                    logout(
                      {},
                      `/login?redirect=${encodeURIComponent(path)}`,
                      false,
                    )
                  }
                >
                  Abmelden
                </button>
              </p>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
};

CheckInPage.path = "/check-in/:code";
CheckInPage.pathWithoutCode = "/check-in";

const SessionCard = ({ session }: { session: CheckInSession }) => (
  <section className="rounded-lg border bg-card p-4">
    <div className="text-xs font-semibold tracking-wide text-muted-foreground">
      SITZUNG HEUTE · AUS DEM KALENDER
    </div>
    <h1 className="mt-1 text-xl font-semibold break-words">{session.title}</h1>
    {session.starts_at ? (
      <div className="mt-1 text-sm text-muted-foreground break-words">
        {[formatSessionWhen(session), session.location]
          .filter(Boolean)
          .join(" · ")}
      </div>
    ) : null}
  </section>
);

const Notice = ({
  icon: Icon,
  tone = "neutral",
  title,
  children,
}: {
  icon: LucideIcon;
  tone?: "neutral" | "success" | "warning";
  title: ReactNode;
  children?: ReactNode;
}) => (
  <section
    role="status"
    className={cn(
      "flex gap-3 rounded-lg border p-4",
      tone === "success" && "border-success/40 bg-success-soft",
      tone === "warning" && "border-warning/50 bg-warning-soft",
    )}
  >
    <Icon
      aria-hidden
      className={cn(
        "mt-0.5 size-6 shrink-0",
        tone === "success"
          ? "text-success"
          : tone === "warning"
            ? "text-warning-text"
            : "text-primary",
      )}
    />
    <div className="min-w-0">
      <h2 className="text-lg font-semibold break-words">{title}</h2>
      {children ? (
        <div className="mt-1 text-sm text-muted-foreground break-words">
          {children}
        </div>
      ) : null}
    </div>
  </section>
);

const ViewBody = ({
  view,
  code,
  busy,
  onCheckIn,
  onReload,
}: {
  view: CheckInView;
  code: string | null;
  busy: boolean;
  onCheckIn: () => void;
  onReload: () => void;
}) => {
  switch (view.kind) {
    case "ready":
      return (
        <div className="flex flex-col gap-3">
          {code ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <QrCode aria-hidden className="size-4 text-success" />
              Raum-Code {code} erkannt
            </p>
          ) : null}
          <Button
            className="h-16 w-full text-lg font-semibold"
            disabled={busy}
            onClick={onCheckIn}
          >
            Ich bin da
          </Button>
          {view.session ? (
            <p className="text-center text-sm text-muted-foreground">
              Eintragen möglich bis{" "}
              {formatTime(checkInWindow(view.session).closes.toISOString())} Uhr
            </p>
          ) : null}
        </div>
      );
    case "checked_in":
      return (
        <Notice
          icon={CheckCircle2}
          tone="success"
          title={`Eingecheckt, ${formatTime(view.checkedInAt)}`}
        >
          {view.session?.billable
            ? `Du stehst auf der ${monthName(view.checkedInAt)}liste für die Bezirksversammlung. Mehr ist nicht zu tun.`
            : `Du bist für „${view.title}“ eingetragen. Mehr ist nicht zu tun.`}
        </Notice>
      );
    case "already":
      return (
        <Notice
          icon={CheckCircle2}
          tone="success"
          title={`Du bist seit ${formatTime(view.checkedInAt)} eingecheckt`}
        >
          Ein Eintrag je Person und Sitzung. Ein zweites Handy ändert nichts.
        </Notice>
      );
    case "too_early":
      return (
        <div className="flex flex-col gap-3">
          <Notice
            icon={Clock}
            title={`Check-in öffnet um ${formatTime(view.opensAt)} Uhr`}
          >
            Eintragen geht ab 30 Minuten vor Beginn. Dann diese Seite neu laden.
          </Notice>
          <Button
            variant="outline"
            className="h-12 w-full text-base"
            onClick={onReload}
          >
            Neu laden
          </Button>
        </div>
      );
    case "window_over":
      return (
        <Notice icon={Clock} tone="warning" title="Eintragen ist vorbei">
          Der Check-in ging bis{" "}
          {formatTime(checkInWindow(view.session).closes.toISOString())} Uhr.
          Warst du da? „Nachtrag anfragen“ geht an das Fraktionsbüro.
        </Notice>
      );
    case "no_code":
      return (
        <Notice icon={QrCode} title="Bitte den QR im Raum scannen">
          Der Link ohne Code trägt niemanden ein. Online dabei oder Handy leer?
          „Nachtrag anfragen“ geht an das Fraktionsbüro.
        </Notice>
      );
    case "cancelled":
      return (
        <Notice
          icon={CalendarX}
          tone="warning"
          title="Die Sitzung ist abgesagt"
        >
          Ein Eintrag ist nicht nötig.
        </Notice>
      );
    case "not_invited":
      return (
        <Notice
          icon={UserX}
          tone="warning"
          title="Du stehst nicht auf der Einladung"
        >
          Eintragen kann sich nur, wer zum Kalendertermin eingeladen ist. Gast?
          Das Fraktionsbüro nimmt dich auf.
        </Notice>
      );
    case "invalid_code":
      return (
        <Notice
          icon={ShieldAlert}
          tone="warning"
          title="Der Raum-Code passt zu keiner Sitzung"
        >
          Bitte den QR im Raum noch einmal scannen.{" "}
          {view.attemptsLeft > 0
            ? `Noch ${view.attemptsLeft} ${view.attemptsLeft === 1 ? "Versuch" : "Versuche"}.`
            : "Danach ist 15 Minuten Pause."}
        </Notice>
      );
    case "too_many_attempts":
      return (
        <Notice icon={ShieldAlert} tone="warning" title="Zu viele Versuche">
          {view.retryAfter
            ? `Wieder möglich ab ${formatTime(view.retryAfter)} Uhr.`
            : "Bitte in 15 Minuten noch einmal versuchen."}
        </Notice>
      );
    case "requested":
      return (
        <Notice icon={CheckCircle2} tone="success" title="Nachtrag angefragt">
          Das Fraktionsbüro sieht deine Anfrage für „{view.session.title}“ und
          entscheidet über den Eintrag.
        </Notice>
      );
    case "error":
      return (
        <div className="flex flex-col gap-3">
          <Notice
            icon={ShieldAlert}
            tone="warning"
            title="Das hat nicht geklappt"
          >
            {view.message}
          </Notice>
          <Button
            variant="outline"
            className="h-12 w-full text-base"
            onClick={onReload}
          >
            Noch einmal versuchen
          </Button>
        </div>
      );
  }
};
